/**
 * Hostile Takeover — MechCorp HQ's unlock (MINIGAME_PROPOSALS O8). You took
 * the headquarters; now you own stock. Buy stakes in five MechCorp divisions
 * with Scrap; they pay Components dividends on a timer. Yields drift on
 * seeded curves, so active players chase the market with one free
 * reallocation per hour; idlers just collect. Buying a division out (stake
 * 10) grants a PERMANENT +5 percent Components, capped at +25 across all
 * five.
 *
 * Design call: multiplicative Components with a hard low ceiling, and the
 * drifting yields roll off pure functions of (seed, time) — no RNG draws, no
 * market state to save.
 */

export interface DivisionDef {
  id: string;
  name: string;
  flavor: string;
  /** Baseline yield factor (HR pays worst, obviously). */
  base: number;
}

export const DIVISIONS: DivisionDef[] = [
  { id: 'div.drones', name: 'MechCorp Drones', flavor: 'Surveillance with a subscription model.', base: 1.2 },
  { id: 'div.munitions', name: 'MechCorp Munitions', flavor: 'Growth sector. Unfortunately.', base: 1.1 },
  { id: 'div.realestate', name: 'MechCorp Real Estate', flavor: 'They own the ruins you fight in.', base: 1.0 },
  { id: 'div.legal', name: 'MechCorp Legal', flavor: 'Sues the wreckage. Sometimes wins.', base: 0.9 },
  { id: 'div.hr', name: 'MechCorp HR', flavor: 'Mandatory fun division. Pays the worst, obviously.', base: 0.6 },
];

export function getDivision(id: string): DivisionDef | undefined {
  return DIVISIONS.find((d) => d.id === id);
}

/** Stake ladder: 0..10; level 10 = the Buyout. */
export const MAX_STAKE = 10;
/** Permanent Components bonus per buyout (5 divisions × 5% = the +25% cap). */
export const BUYOUT_COMPONENT_BONUS = 0.05;

/** Dividends pay automatically once per this period. */
export const DIVIDEND_MS = 30 * 60_000;
/** Components per stake level per payout, × yield × the frontier anchor. */
export const DIVIDEND_RATE = 0.25;
/** Bought-out divisions stop drifting and pay this fixed yield. */
export const BUYOUT_YIELD = 1.25;

/** One free stake reallocation per this period (the idle-friendly cadence). */
export const SHIFT_COOLDOWN_MS = 60 * 60_000;

/** Scrap cost factor for the next stake level (× waveScrap(highestWave) × (level+1)²). */
export const STAKE_COST_MULT = 40;

/** Yield drift: two slow sines, amplitudes and periods. */
export const DRIFT_A1 = 0.25;
export const DRIFT_P1_MS = 3 * 60 * 60_000;
export const DRIFT_A2 = 0.15;
export const DRIFT_P2_MS = 47 * 60_000;
